/**
 * @file The Timeline service
 * @author Willem Jan Weitering
 */
import { AlertsService } from '../core/alerts.service';
import { TimelineItemType } from './helpers/timelineItem-type.enum';
import { OverlayService } from '../core/overlay.service';
import { ResourceService } from '../resource.service';
/**
 * Class for the timeline service
 */
var TimelineService = /** @class */ (function () {
    /**
     * Constructs a TimelineService
     */
    function TimelineService(alertsService, overlayService, resourceService) {
        this.alertsService = alertsService;
        this.overlayService = overlayService;
        this.resourceService = resourceService;
        /* the timeline items */
        this._timelineItems = undefined;
        /* Total available timeline items */
        this.totalItems = undefined;
        /* incrementing id for next timeline item to be created in createTimelineItem */
        this.lastId = 0;
        /* timer for adding new timeline item every now and then */
        this.timelineTimer = undefined;
        /* Data for creating (random) items */
        this.lines = [
            'Lorem ipsum dolor sit amet, consectetur adipiscing elit.',
            'Praesent eu nisi id nisl vulputate aliquet id at risus.',
            'Aliquam nec dolor nec massa luctus tempor.',
            'Sed et massa ac lacus varius commodo.',
            'Nullam sit amet sapien a sapien placerat eleifend a eu dui.',
            'Praesent ultricies est bibendum, varius lacus a, ornare diam.',
            'Sed eleifend leo pharetra lorem efficitur, ut luctus orci aliquam.',
            'Nullam ac purus vel metus tempor euismod.',
            'Vestibulum at elit vitae leo faucibus tempus eget vitae neque.',
            'Aenean vitae est in felis hendrerit feugiat eget eget sapien.',
            'Nullam in arcu ut ligula tempus rutrum.',
            'Sed ut est feugiat, bibendum leo sed, placerat orci.',
            'Nunc laoreet neque sit amet justo interdum, laoreet tempor enim egestas.',
            'Phasellus sed lectus non elit efficitur pulvinar.',
            'Sed laoreet elit vestibulum ullamcorper varius.',
            'Nullam aliquet purus ac elementum rhoncus.',
            'Pellentesque venenatis nisi ut nisl venenatis ornare.',
            'Sed at felis ac orci porttitor vestibulum.',
            'Pellentesque ut quam et odio feugiat molestie.',
            'Integer a ex et eros dignissim fringilla in sed ligula.',
            'Mauris vitae lectus tristique, faucibus ligula at, fringilla tortor.',
            'Vivamus et nunc eget sapien tristique convallis.',
            'Pellentesque rutrum est ut purus sollicitudin, id dapibus ligula dapibus.',
            'Sed eu lorem ultrices, tincidunt ex ut, auctor urna.',
            'Praesent ac tortor a nisi semper convallis.',
            'Donec dictum nisi ac diam auctor, lacinia rutrum nisl semper.',
            'Pellentesque vitae libero vel ante venenatis pellentesque nec at ante.',
            'Suspendisse faucibus odio et quam pulvinar, vel suscipit risus blandit.',
            'Sed a urna feugiat, scelerisque metus quis, aliquam sapien.',
            'Phasellus quis arcu tristique, elementum tellus a, dapibus augue.',
        ];
        this.authors = [
            {
                fullName: 'Albert Kremer',
                imageLink: 'https://randomuser.me/api/portraits/men/1.jpg'
            },
            {
                fullName: 'Gea Stevens-Meekma',
                imageLink: 'https://randomuser.me/api/portraits/women/2.jpg'
            },
            {
                fullName: 'Diederik Kraaikamp',
                imageLink: 'https://randomuser.me/api/portraits/men/2.jpg'
            },
            {
                fullName: 'Hans van der Snoek',
                imageLink: 'https://randomuser.me/api/portraits/men/3.jpg'
            },
            {
                fullName: 'Maarten Lensink',
                imageLink: 'https://randomuser.me/api/portraits/men/4.jpg'
            },
            {
                fullName: 'Heino Lenting',
                imageLink: 'https://randomuser.me/api/portraits/men/5.jpg'
            },
            {
                fullName: 'Simon de Jonge',
                imageLink: 'https://randomuser.me/api/portraits/men/6.jpg'
            },
            {
                fullName: 'Peter Brouwer',
                imageLink: 'https://randomuser.me/api/portraits/men/7.jpg'
            },
            {
                fullName: 'Tjark Mulder',
                imageLink: 'https://randomuser.me/api/portraits/men/8.jpg'
            },
            {
                fullName: 'Mëriam van Veen',
                imageLink: 'https://randomuser.me/api/portraits/women/3.jpg'
            },
            {
                fullName: 'Miranda Derks',
                imageLink: 'https://randomuser.me/api/portraits/women/4.jpg'
            },
            {
                fullName: 'Louise Revens',
                imageLink: 'https://randomuser.me/api/portraits/women/5.jpg'
            }
        ];
        this.randomtypes = [
            {
                type: TimelineItemType.appointment,
                titles: ['Upcoming appointment']
            },
            {
                type: TimelineItemType.mind,
                titles: [' says']
            },
            {
                type: TimelineItemType.notification,
                titles: ['New notification']
            },
            {
                type: TimelineItemType.planning,
                titles: [
                    'Short term change in planning',
                    'Long term change in planning'
                ]
            },
            {
                type: TimelineItemType.profile,
                titles: [
                    'New profile picture',
                    'New profile competences',
                    'New profile info',
                    'New profile locations'
                ],
            },
            {
                type: TimelineItemType.project,
                titles: [
                    'Project started',
                    'Project ended'
                ]
            },
            {
                type: TimelineItemType.task,
                titles: ['Impending task']
            }
        ];
        this.defaultAuthor = {
            fullName: 'Peter Dinklage',
            imageLink: 'https://randomuser.me/api/portraits/men/10.jpg'
        };
        this.longNameAuthor = {
            fullName: 'Tom Verschoore de la Houssaije',
            imageLink: 'https://randomuser.me/api/portraits/men/9.jpg'
        };
    }
    /**
     * Used for generating demo data
     */
    TimelineService.prototype.fillDemoData = function () {
        var _this = this;
        /* fill time values for demo appointment- en task timeline items */
        var appointmentCreated = Date.now() - Math.floor(Math.random() * 1000 * 60 * 24 * 4) + 15000;
        var appointmentStart = new Date(appointmentCreated + 1000 * 60 * 24);
        appointmentStart.setMinutes(0);
        var taskCreated = Date.now() - Math.floor(Math.random() * 1000 * 60 * 24 * 4);
        var taskStart = new Date(taskCreated + 1000 * 60 * 60 * 2);
        taskStart.setMinutes(0);
        var taskFinish = new Date(taskStart.getTime() + 1000 * 60 * 60 * 5);
        /* Demo data */
        this._timelineItems = [
            {
                id: 1,
                type: TimelineItemType.profile,
                title: 'New profile picture!',
                text: 'Your colleague [Tom Verschoore de la Houssaije](#) has a new profile picture. Check it out!',
                created: new Date(Date.now() - Math.floor(Math.random() * 1000 * 60 * 24 * 20) + 15000),
                author: this.defaultAuthor,
                imageLink: this.longNameAuthor.imageLink,
                start: null,
                finish: null
            },
            {
                id: 2,
                type: TimelineItemType.mind,
                title: 'Tom Verschoore de la Houssaye says:',
                text: 'Project \'Refactoring HTML5\' is finished in time and budget. Let\'s celebrate this friday. Drink\'s on me!!!',
                created: new Date(Date.now() - Math.floor(Math.random() * 1000 * 60 * 24 * 20) + 15000),
                author: this.longNameAuthor,
                imageLink: null,
                start: null,
                finish: null
            },
            {
                id: 3,
                type: TimelineItemType.project,
                title: 'Project started',
                text: 'Project \'HTML5\' for customer \'Symagic\' is started! Look at the details of this project.',
                created: new Date(Date.now() - Math.floor(Math.random() * 1000 * 60 * 24 * 20) + 15000),
                author: this.defaultAuthor,
                imageLink: null,
                start: null,
                finish: null
            },
            {
                id: 4,
                type: TimelineItemType.task,
                title: 'Impending task',
                text: 'The task \'Start working on HTML5\' under project \'HTML 5\' is starting in the next few hours.',
                created: new Date(taskCreated),
                author: this.defaultAuthor,
                imageLink: null,
                start: taskStart,
                finish: taskFinish
            },
            {
                id: 5,
                type: TimelineItemType.appointment,
                title: 'Upcoming appointment',
                // tslint:disable-next-line:max-line-length
                text: 'You have an upcoming appointment in about an hour with name [\'Appointment with stakeholders\'](# "Appointment with stakeholders at '
                    + new Date(appointmentStart).toTimeString().substring(0, 5) + '") under project \'HTML5\'.',
                created: new Date(appointmentCreated),
                author: this.defaultAuthor,
                imageLink: null,
                start: appointmentStart,
                finish: null
            },
            {
                id: 6,
                type: TimelineItemType.notification,
                title: 'New notification',
                text: 'You received a new notification with title \'Change request for assignment\' with priority \'High\'.',
                created: new Date(Date.now() - Math.floor(Math.random() * 1000 * 60 * 24 * 20) + 15000),
                author: this.defaultAuthor,
                imageLink: null,
                start: null,
                finish: null
            },
            {
                id: 7,
                type: TimelineItemType.planning,
                title: 'Short-term change in planning',
                text: 'The task \'Start working on HTML5\' under project \'HTML5\' is changed! Check out the [new details](#) here.',
                created: new Date(Date.now() - Math.floor(Math.random() * 1000 * 60 * 24 * 20) + 15000),
                author: this.defaultAuthor,
                imageLink: null,
                start: null,
                finish: null
            },
            {
                id: 8,
                type: TimelineItemType.realisation,
                title: 'Realisation missing',
                text: 'In the previous week there are assignments and/or appointments unrealised. Please [correct](#) this.',
                created: new Date(Date.now() - Math.floor(Math.random() * 1000 * 60 * 24 * 20) + 15000),
                author: this.defaultAuthor,
                imageLink: null,
                start: null,
                finish: null
            },
            {
                id: 9,
                type: TimelineItemType.holidays,
                title: 'Holidays',
                text: 'In this year you have 23.2 days available for holidays. Check here your planned holidays.',
                created: new Date(Date.now() - Math.floor(Math.random() * 1000 * 60 * 24 * 20) + 15000),
                author: this.defaultAuthor,
                imageLink: null,
                start: null,
                finish: null
            }
        ];
        this.lastId = 9;
        // Quick sort
        this._timelineItems.sort(function (a, b) {
            return +b.created - +a.created;
        });
        /* Set inline count */
        this.totalItems = this._timelineItems.length;
        /* start timer for creating random new item in a while */
        this.timelineTimer = setTimeout(function () { _this.addRandomTimelineItem(); }, 30 * 1000);
    };
    /**
     * Create a timeline item and add to timelineitems queue
     * @param type The timeline-item type (project | profile | appointment | task | notification | planning | mind | realisation | holidays)
     * @param title The title of of the timeline-item
     * @param text The message text of the timeline-item
     * @param created The created date-time of the timeline-item
     * @param author The author text of the timeline-item
     * @param start start date-time of the timeline-item
     * @param finish The finish date-time of the timeline-item
     */
    TimelineService.prototype.createTimelineItem = function (type, title, text, created, author, imagelink, start, finish) {
        // Increase lastId
        this.lastId += 1;
        // Store new id
        var id = this.lastId;
        // Create timeline item
        var timelineItem = {
            'id': id,
            'type': type,
            'title': title,
            'text': text,
            'created': created,
            'author': author,
            'imageLink': imagelink,
            'start': start,
            'finish': finish
        };
        // Add to timeline-items
        this._timelineItems.unshift(timelineItem);
        this.totalItems = this._timelineItems.length;
    };
    TimelineService.prototype.addRandomTimelineItem = function () {
        var _this = this;
        /* stop the timer */
        clearTimeout(this.timelineTimer);
        /* pick a type */
        var type = this.randomtypes[Math.floor(Math.random() * this.randomtypes.length)];
        /* pick a title */
        var title = type.titles[Math.floor(Math.random() * type.titles.length)];
        /* pick a text */
        var text = '';
        var max = 1 + Math.floor(Math.random() * 3);
        for (var i = 1; i <= max; i++) {
            text += this.lines[Math.floor(Math.random() * this.lines.length)];
        }
        /* pick an author */
        var author = this.defaultAuthor;
        /* handle type == mind */
        if (type.type === TimelineItemType.mind) {
            author = this.authors[Math.floor(Math.random() * this.authors.length)];
            title = author.fullName + type.titles[0];
        }
        var imagelink = null;
        /* handle type == profile */
        if (type.type === TimelineItemType.profile) {
            imagelink = this.authors[Math.floor(Math.random() * this.authors.length)].imageLink;
        }
        /* set timevalues */
        var created = new Date(Date.now());
        var start = new Date(created.getTime() + 1000 * 60 * 60 * 1);
        start.setMinutes(0);
        var finish = new Date(start.getTime() + 1000 * 60 * 60 * 1 + Math.floor(Math.random() * 8));
        finish.setMinutes(0);
        /* create the timelineItem */
        this.createTimelineItem(type.type, title, text, created, author, imagelink, start, finish);
        /* schedule the next creation in 2 minutes or more */
        this.timelineTimer = setTimeout(function () { _this.addRandomTimelineItem(); }, Math.random() * 2 * 60 * 1000 + 2 * 60 * 1000);
        // this.timelineTimer = setTimeout(() => { this.addRandomTimelineItem(); }, Math.random() * 10 *1000); //just for testing
    };
    /**
     * Gets requested timeline items
     * @param begin begin position in timeline items array of items to deliver
     * @param numberOfItems number of items to deliver
     */
    TimelineService.prototype.getTimelineItems = function (begin, numberOfItems) {
        if (!this._timelineItems) {
            this.fillDemoData();
        }
        return this._timelineItems.slice(begin, begin + numberOfItems);
    };
    /**
     * Delete the timeline-item from the timeline-items queue
     * @param id The id of the timeline-item
     */
    TimelineService.prototype.deleteTimelineItem = function (id) {
        var index = this.findIndex(id);
        // Remove timeline-item from timeline-items
        if (index !== undefined && this._timelineItems.length >= index) {
            this._timelineItems.splice(index, 1);
            // Inform user about deletion
            this.alertsService.createAlert('success', 'Timeline bericht met succes verwijderd');
            this.totalItems = this._timelineItems.length;
        }
    };
    /**
     * Translate a timeline-item id to the position (index) in the queue
     * @param id The id of the timeline-item
     * @returns The position in the queue
     */
    TimelineService.prototype.findIndex = function (id) {
        var index;
        // Find the timeline-item based on id
        this._timelineItems.forEach(function (timelineItem, i) {
            if (timelineItem.id === id) {
                index = i;
            }
        });
        return index;
    };
    return TimelineService;
}());
export { TimelineService };
